import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'assets/icons';

/**
 * A select whose options carry an icon, a title and a description, with a
 * check on the active one (identity validation: Identité / Permis-CACES).
 *
 * options: [{ value, label, description, icon, iconClassName }]
 * Closes on an outside click, on Escape and on Tab. From the button, ArrowDown,
 * ArrowUp, Enter and Space open the menu; in the menu the arrows, Home and End
 * move, Enter and Space choose.
 */
const DocumentTypeSelect = ({
  options = [],
  value,
  onChange,
  ariaLabel,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);
  const listboxId = useId();

  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const selectedOption = options[selectedIndex];

  const open = (index = selectedIndex) => {
    setFocusedIndex(index);
    setIsOpen(true);
  };

  const close = ({ restoreFocus = false } = {}) => {
    setIsOpen(false);
    setFocusedIndex(-1);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const choose = (option) => {
    close({ restoreFocus: true });
    if (option && option.value !== value) onChange?.(option.value);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (
        !containerRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  // The menu is portalled to <body> with a fixed position: inside the
  // SubNavbar's scroll container (overflow-y hidden) it was clipped, and the
  // bar scrolled to show it. A fixed menu cannot follow a scroll, so it closes.
  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setMenuPosition({ top: rect.bottom + 4, left: rect.left });
    const closeOnMove = (event) => {
      if (!menuRef.current?.contains(event.target)) close();
    };
    window.addEventListener('resize', closeOnMove);
    window.addEventListener('scroll', closeOnMove, true);
    return () => {
      window.removeEventListener('resize', closeOnMove);
      window.removeEventListener('scroll', closeOnMove, true);
    };
  }, [isOpen]);

  // menuPosition: the options only exist once the menu is positioned.
  useEffect(() => {
    if (isOpen && menuPosition && focusedIndex >= 0) {
      optionRefs.current[focusedIndex]?.focus();
    }
  }, [isOpen, focusedIndex, menuPosition]);

  const onButtonKeyDown = (event) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (isOpen && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        close();
        return;
      }
      open(selectedIndex);
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      close();
    }
  };

  const onListKeyDown = (event) => {
    const last = options.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex((index) => (index >= last ? 0 : index + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex((index) => (index <= 0 ? last : index - 1));
        break;
      case 'Home':
        event.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setFocusedIndex(last);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(options[focusedIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        close({ restoreFocus: true });
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className={`relative w-[200px] ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={ariaLabel}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={onButtonKeyDown}
        className={`w-full h-10 flex items-center justify-between gap-2 px-3 text-sm text-gray-900 bg-white border rounded-[0.4rem] transition-colors hover:bg-gray-50 focus:outline-none focus:border-yellow-500 ${
          isOpen ? 'border-yellow-500' : 'border-gray-300'
        }`}
      >
        <span className="truncate">{selectedOption?.label}</span>
        <span
          className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <ChevronDown wh={16} color="#6B7280" />
        </span>
      </button>

      {isOpen && menuPosition && createPortal(
        <ul
          ref={menuRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          onKeyDown={onListKeyDown}
          style={{ top: menuPosition.top, left: menuPosition.left }}
          className="fixed z-[60] w-[336px] max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-100 p-1 origin-top-left motion-safe:animate-menuIn"
        >
          {options.map((option, index) => {
            const isSelected = option.value === selectedOption?.value;
            return (
              <li
                key={option.value}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                onClick={() => choose(option)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`flex items-start gap-3 px-3 py-2.5 rounded-md cursor-pointer outline-none transition-colors ${
                  index === focusedIndex ? 'bg-gray-100' : ''
                } ${isSelected && index !== focusedIndex ? 'bg-gray-50' : ''}`}
              >
                {option.icon && (
                  <span
                    aria-hidden="true"
                    className={`shrink-0 mt-0.5 w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center ${
                      option.iconClassName || 'bg-white text-gray-700'
                    }`}
                  >
                    {option.icon}
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-900">
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {option.description}
                    </span>
                  )}
                </span>
                {isSelected && (
                  <span
                    data-testid="document-type-check"
                    aria-hidden="true"
                    className="shrink-0 self-center"
                  >
                    <Check wh={18} color="#FF9600" />
                  </span>
                )}
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default DocumentTypeSelect;
